import { lazy, Suspense } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  TIMELINE,
  groupByCategory,
  type Milestone,
} from "~/lib/build/timeline";

const BuildStage = lazy(() =>
  import("~/three/BuildStage").then((m) => ({ default: m.BuildStage })),
);

export function Build() {
  const face = FACE_BY_ID.build;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);
  const grouped = groupByCategory(TIMELINE);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-56 w-full overflow-hidden border-b border-jg-border/40 sm:h-72">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-build/15 to-transparent"
            />
          }
        >
          <BuildStage />
        </Suspense>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-4xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          Ten years of <span style={{ color: face.themeColor }}>shipping</span>
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          One engineer, eight disciplines, a career spent at the seam
          between native mobile and immersive tech.
        </p>
      </header>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4">
        <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
          <p
            className="font-mono text-[10px] uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            About
          </p>
          <h2 className="mt-2 font-display text-xl font-semibold">
            João Vidotto
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted">
            Senior mobile engineer based in Londrina, Brazil. Bilingual
            PT-BR / EN. Ships across Unity (C#), Unreal (C++), Go,
            native Android (Kotlin / Jetpack Compose), React Native, AR
            (Vuforia, AR Foundation), and now a lot of LLM integration
            work — including this site you're on.
          </p>
        </div>

        {CATEGORY_ORDER.map((cat) => {
          const items = grouped[cat];
          if (items.length === 0) return null;
          return (
            <div key={cat} className="flex flex-col gap-3">
              <h3
                className="font-mono text-xs uppercase tracking-[0.25em]"
                style={{ color: face.themeColor }}
              >
                {CATEGORY_LABELS[cat]}
              </h3>
              <ul className="grid gap-3 sm:grid-cols-2">
                {items.map((m) => (
                  <li key={m.id}>
                    <MilestoneCard milestone={m} themeColor={face.themeColor} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface MilestoneCardProps {
  milestone: Milestone;
  themeColor: string;
}

function MilestoneCard({ milestone, themeColor }: MilestoneCardProps) {
  const card = (
    <div className="flex h-full flex-col gap-2 rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-4 transition-colors group-hover:border-jg-build/40">
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="font-display text-base font-semibold leading-tight">
          {milestone.title}
        </h4>
        {milestone.year && (
          <span
            className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: themeColor }}
          >
            {milestone.year}
          </span>
        )}
      </div>
      <p className="text-sm leading-relaxed text-jg-fg-muted">
        {milestone.body}
      </p>
      {milestone.tags && milestone.tags.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {milestone.tags.map((t) => (
            <li
              key={t}
              className="rounded-full border border-jg-border bg-jg-bg/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-jg-fg-muted"
            >
              {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (milestone.link) {
    return (
      <a
        href={milestone.link}
        target="_blank"
        rel="noopener noreferrer"
        className="group block h-full"
      >
        {card}
      </a>
    );
  }

  return <div className="group block h-full">{card}</div>;
}
